import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import { toast } from "sonner";
import { Users, TrendingUp, Wallet, Loader2, Search, ShieldCheck, Mail, Send, Eye, X, Zap, Clock, Calendar, Archive, KeyRound, Trash2, UserPlus, Inbox } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending", label: "In attesa", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "confirmed", label: "Confermato", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "contacted", label: "Contattato", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "subscribed", label: "Sottoscritto", color: "bg-green-50 text-green-700 border-green-200" },
  { value: "expired", label: "Scaduto", color: "bg-slate-100 text-slate-600 border-slate-200" },
  { value: "rejected", label: "Rifiutato", color: "bg-red-50 text-red-700 border-red-200" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/referrals", { params: { status: filter || undefined, q: q || undefined } }),
      ]);
      setStats(s.data);
      setReferrals(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (rid, status) => {
    try {
      await api.patch(`/admin/referrals/${rid}/status`, { status });
      toast.success("Stato aggiornato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F9]" data-testid="admin-page">
      <Header variant="app" />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-[#0F7B85]" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Backoffice</div>
            <h1 className="font-display text-3xl text-[#0F7B85] tracking-tight">Pannello amministratore</h1>
          </div>
        </div>

        {/* KPI */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Kpi label="Totale segnalazioni" value={stats?.total_referrals ?? "—"} icon={Users} testid="kpi-total"/>
          <Kpi label="Conversion rate" value={stats ? `${stats.conversion_rate}%` : "—"} icon={TrendingUp} testid="kpi-conv" accent/>
          <Kpi label="Crediti erogati" value={stats ? `${stats.credits_issued}€` : "—"} icon={Wallet} testid="kpi-credits"/>
          <Kpi label="Clienti attivi" value={stats?.customers ?? "—"} icon={Users} testid="kpi-customers"/>
        </div>

        {/* By status */}
        {stats && (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Distribuzione stati</div>
            <div className="flex flex-wrap gap-3">
              <FilterChip label="Tutti" value="" current={filter} onClick={setFilter} count={stats.total_referrals}/>
              {STATUS_OPTIONS.map(s => (
                <FilterChip key={s.value} label={s.label} value={s.value} current={filter} onClick={setFilter} count={stats.by_status[s.value] || 0}/>
              ))}
            </div>
          </div>
        )}

        {/* Weekly digest */}
        <DigestPanel />

        {/* Email automations */}
        <AutomationsPanel />

        {/* Programma referral — iscrizioni post-rinnovo RCA */}
        <ProgramEnrollPanel />

        {/* Maintenance & sicurezza */}
        <MaintenancePanel onCleanupDone={load} />

        {/* Search & table */}
        <section className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Cerca per targa, email, nome…"
                data-testid="admin-search-input"
                className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
              />
            </div>
            <button
              onClick={load} data-testid="admin-search-button"
              className="bg-[#0F7B85] text-white hover:bg-[#0A5C64] rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
            >
              Filtra
            </button>
          </div>

          {loading ? (
            <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Caricamento…</div>
          ) : referrals.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center text-slate-500" data-testid="admin-empty">
              Nessuna segnalazione corrisponde ai filtri.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
              <table className="w-full min-w-[900px]" data-testid="admin-referrals-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="py-3 px-4 text-left">Cliente</th>
                    <th className="py-3 px-4 text-left">Targa</th>
                    <th className="py-3 px-4 text-left">Amico</th>
                    <th className="py-3 px-4 text-left">Data</th>
                    <th className="py-3 px-4 text-left">Stato</th>
                    <th className="py-3 px-4 text-right">Cambia stato</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`admin-row-${r.id}`}>
                      <td className="py-4 px-4 text-sm">
                        <div className="font-medium text-slate-800">{r.customer_name}</div>
                        <div className="text-slate-500 text-xs">{r.customer_email}</div>
                      </td>
                      <td className="py-4 px-4 font-mono text-sm uppercase text-[#0F7B85]">{r.plate || <span className="text-slate-400 normal-case font-sans">—</span>}</td>
                      <td className="py-4 px-4 text-sm">
                        <div className="font-medium text-slate-800">{r.friend_name}</div>
                        <div className="text-slate-500 text-xs">{r.friend_email}</div>
                        <div className="text-slate-500 text-xs">{r.friend_phone}</div>
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-600">
                        {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="py-4 px-4">
                        <StatusBadge status={r.status} />
                        {r.credit_awarded && (
                          <div className="text-xs text-green-700 mt-1">+10€ erogati</div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <select
                          value={r.status}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          data-testid={`admin-status-select-${r.id}`}
                          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl p-6 border ${accent ? "bg-[#0F7B85] text-white border-[#0F7B85]" : "bg-white border-slate-200"}`}
    >
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</div>
        <Icon className={`w-5 h-5 ${accent ? "text-[#FCD9A0]" : "text-[#0F7B85]"}`}/>
      </div>
      <div className={`mt-4 font-display text-4xl ${accent ? "" : "text-[#0F7B85]"}`}>{value}</div>
    </div>
  );
}

function FilterChip({ label, value, current, onClick, count }) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      data-testid={`filter-chip-${value || "all"}`}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active ? "bg-[#0F7B85] text-white border-[#0F7B85]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0F7B85]"
      }`}
    >
      {label} <span className="opacity-70 ml-1">{count}</span>
    </button>
  );
}

function DigestPanel() {
  const [last, setLast] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadLast = async () => {
    try {
      const { data } = await api.get("/admin/digest/last-run");
      setLast(data);
    } catch { /* noop */ }
  };

  useEffect(() => { loadLast(); }, []);

  const sendNow = async () => {
    if (!confirm("Inviare il digest settimanale a tutti i clienti idonei?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(`Digest inviato: ${data.sent} email · ${data.skipped} saltati · ${data.failed} falliti`);
      await loadLast();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio digest");
    } finally {
      setSending(false);
    }
  };

  const preview = async () => {
    if (!previewEmail) {
      toast.error("Inserisci l'email del cliente");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/digest/preview?customer_email=${encodeURIComponent(previewEmail)}`);
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore preview");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="digest-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#F39C12]"/> Digest settimanale
          </div>
          <h3 className="font-display text-xl text-[#0F7B85] mt-1">Riepilogo automatico ai clienti</h3>
          <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
            Email personalizzata con saldo, prossimo badge, posizione leaderboard e segnalazioni in corso.
            Aumenta la partecipazione e tiene caldo il programma.
          </p>
          {last?.at && (
            <div className="mt-3 text-xs text-slate-500" data-testid="digest-last-run">
              Ultimo invio: <strong>{new Date(last.at).toLocaleString("it-IT")}</strong> ·
              {" "}<span className="text-green-700">{last.sent} inviati</span> ·
              {" "}<span className="text-slate-500">{last.skipped} saltati</span>
              {last.failed > 0 && <> · <span className="text-red-700">{last.failed} falliti</span></>}
              {last.triggered_by && <> · <span className="text-slate-400">{last.triggered_by === "cron" ? "automatico" : "manuale"}</span></>}
            </div>
          )}
          {last?.cron_enabled !== undefined && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs" data-testid="digest-cron-status">
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-medium ${last.cron_enabled ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${last.cron_enabled ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                Cron {last.cron_enabled ? "attivo" : "disattivo"}
              </span>
              <span className="text-slate-500">
                Pianificato: <strong className="text-slate-700">{last.cron_schedule}</strong>
              </span>
              {last.next_run_at && (
                <span className="text-slate-500">
                  · Prossimo invio: <strong className="text-[#0F7B85]">{new Date(last.next_run_at).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</strong>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2">
            <input
              type="email"
              value={previewEmail}
              onChange={(e) => setPreviewEmail(e.target.value)}
              placeholder="email@cliente.it"
              data-testid="digest-preview-email"
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none w-56"
            />
            <button
              onClick={preview} disabled={previewing} data-testid="digest-preview-button"
              className="bg-white border border-slate-200 hover:border-[#0F7B85] text-slate-700 hover:text-[#0F7B85] rounded-lg px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Eye className="w-4 h-4"/>} Anteprima
            </button>
          </div>
          <button
            onClick={sendNow} disabled={sending} data-testid="digest-send-button"
            className="bg-[#F39C12] text-white hover:bg-[#E08E10] disabled:opacity-60 rounded-lg px-5 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 justify-center"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            Invia ora a tutti
          </button>
        </div>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" data-testid="digest-preview-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display text-lg text-[#0F7B85]">Anteprima email digest</div>
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="digest-preview-close"
                className="text-slate-500 hover:text-[#F39C12] p-1"
              >
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="overflow-auto p-1 bg-slate-100">
              <iframe
                title="digest-preview"
                srcDoc={previewHtml}
                className="w-full bg-white"
                style={{ height: "70vh", border: "none" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-block text-xs px-2.5 py-1 rounded-full border ${meta.color}`}>{meta.label}</span>
  );
}


function AutomationsPanel() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState({ customer: false, friend: false, expiry: false });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/automations/status");
      setStatus(data);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const runOne = async (kind) => {
    const labels = {
      customer: "il reminder ai clienti inattivi",
      friend: "il reminder agli amici in sospeso",
      expiry: "la scadenza automatica dei referral pending (>12 mesi)",
    };
    if (!window.confirm(`Inviare ora ${labels[kind]}?`)) return;
    setRunning((s) => ({ ...s, [kind]: true }));
    try {
      const path = kind === "customer" ? "customer-reminder" : kind === "friend" ? "friend-reminder" : "referral-expiry";
      const { data } = await api.post(`/admin/automations/${path}/run`);
      const summary = kind === "expiry"
        ? `Scaduti ${data.expired} · notificati ${data.notified} · falliti ${data.failed}`
        : `Inviati ${data.sent} · saltati ${data.skipped} · falliti ${data.failed}`;
      toast.success(summary);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore esecuzione");
    } finally {
      setRunning((s) => ({ ...s, [kind]: false }));
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const fmtRelative = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="automations-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        <Zap className="w-4 h-4 text-[#F39C12]"/> Automation Email
      </div>
      <h3 className="font-display text-xl text-[#0F7B85] mt-1">Reminder automatici</h3>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Due cron job per aumentare il tasso di conversione: ricorda al cliente di segnalare un amico
        e al suo amico di richiedere il preventivo se non risponde.
      </p>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Customer reminder */}
        <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-4" data-testid="customer-reminder-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0F7B85]"/>
              <div className="font-semibold text-slate-800">Reminder cliente inattivo</div>
            </div>
            {status?.customer_reminder?.enabled !== undefined && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${status.customer_reminder.enabled ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.customer_reminder.enabled ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                {status.customer_reminder.enabled ? "Attivo" : "Disattivato"}
              </span>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-600 leading-relaxed">
            Inviato {status?.customer_reminder?.hours_after_visit || 24}h dopo che un cliente apre la dashboard
            senza creare una segnalazione. Cooldown {status?.customer_reminder?.cooldown_days || 30} giorni.
          </div>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            <div>Controllo: <strong className="text-slate-700">ogni ora</strong>
              {status?.customer_reminder?.next_run_at && <> · Prossimo: <strong className="text-[#0F7B85]">{fmtDate(status.customer_reminder.next_run_at)}</strong></>}
            </div>
            {status?.customer_reminder?.last_run?.at && (
              <div data-testid="customer-reminder-last-run">
                Ultimo run: <strong>{fmtRelative(status.customer_reminder.last_run.at)}</strong> ·
                {" "}<span className="text-green-700">{status.customer_reminder.last_run.sent} inviati</span>
                {status.customer_reminder.last_run.failed > 0 && <> · <span className="text-red-700">{status.customer_reminder.last_run.failed} falliti</span></>}
              </div>
            )}
          </div>
          <button
            onClick={() => runOne("customer")} disabled={running.customer}
            data-testid="customer-reminder-run-button"
            className="mt-4 bg-white border border-slate-200 hover:border-[#0F7B85] text-slate-700 hover:text-[#0F7B85] rounded-lg px-4 py-1.5 text-xs font-medium transition-colors inline-flex items-center gap-1.5"
          >
            {running.customer ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
            Esegui ora
          </button>
        </div>

        {/* Friend reminder */}
        <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-4" data-testid="friend-reminder-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#F39C12]"/>
              <div className="font-semibold text-slate-800">Reminder amico segnalato</div>
            </div>
            {status?.friend_reminder?.enabled !== undefined && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${status.friend_reminder.enabled ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.friend_reminder.enabled ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                {status.friend_reminder.enabled ? "Attivo" : "Disattivato"}
              </span>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-600 leading-relaxed">
            Inviato {status?.friend_reminder?.days_after_invite || 7} giorni dopo l&apos;invito,
            solo se la segnalazione è ancora <em>pending</em>. Una sola volta per referral.
          </div>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            <div>Pianificato: <strong className="text-slate-700">{status?.friend_reminder?.schedule || "—"}</strong>
              {status?.friend_reminder?.next_run_at && <> · Prossimo: <strong className="text-[#0F7B85]">{fmtDate(status.friend_reminder.next_run_at)}</strong></>}
            </div>
            {status?.friend_reminder?.last_run?.at && (
              <div data-testid="friend-reminder-last-run">
                Ultimo run: <strong>{fmtRelative(status.friend_reminder.last_run.at)}</strong> ·
                {" "}<span className="text-green-700">{status.friend_reminder.last_run.sent} inviati</span>
                {status.friend_reminder.last_run.failed > 0 && <> · <span className="text-red-700">{status.friend_reminder.last_run.failed} falliti</span></>}
              </div>
            )}
          </div>
          <button
            onClick={() => runOne("friend")} disabled={running.friend}
            data-testid="friend-reminder-run-button"
            className="mt-4 bg-white border border-slate-200 hover:border-[#F39C12] text-slate-700 hover:text-[#F39C12] rounded-lg px-4 py-1.5 text-xs font-medium transition-colors inline-flex items-center gap-1.5"
          >
            {running.friend ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
            Esegui ora
          </button>
        </div>

        {/* Referral expiry */}
        <div className="bg-[#F7F8F9] border border-slate-200 rounded-xl p-4" data-testid="referral-expiry-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Archive className="w-4 h-4 text-slate-600"/>
              <div className="font-semibold text-slate-800">Scadenza referral</div>
            </div>
            {status?.referral_expiry?.enabled !== undefined && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${status.referral_expiry.enabled ? "bg-green-50 text-green-700 border border-green-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.referral_expiry.enabled ? "bg-green-500 animate-pulse" : "bg-slate-400"}`} />
                {status.referral_expiry.enabled ? "Attivo" : "Disattivato"}
              </span>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-600 leading-relaxed">
            Marca come <em>scaduti</em> i referral in <em>pending</em> da oltre {status?.referral_expiry?.expiry_days || 365} giorni.
            Applica il regolamento &ldquo;Besafe ti premia&rdquo; (validità 12 mesi).
          </div>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            <div>Pianificato: <strong className="text-slate-700">{status?.referral_expiry?.schedule || "—"}</strong>
              {status?.referral_expiry?.next_run_at && <> · Prossimo: <strong className="text-[#0F7B85]">{fmtDate(status.referral_expiry.next_run_at)}</strong></>}
            </div>
            {status?.referral_expiry?.last_run?.at && (
              <div data-testid="referral-expiry-last-run">
                Ultimo run: <strong>{fmtRelative(status.referral_expiry.last_run.at)}</strong> ·
                {" "}<span className="text-slate-700">{status.referral_expiry.last_run.expired} scaduti</span>
                {status.referral_expiry.last_run.notified > 0 && <> · <span className="text-[#0F7B85]">{status.referral_expiry.last_run.notified} notificati</span></>}
                {status.referral_expiry.last_run.failed > 0 && <> · <span className="text-red-700">{status.referral_expiry.last_run.failed} falliti</span></>}
              </div>
            )}
          </div>
          <button
            onClick={() => runOne("expiry")} disabled={running.expiry}
            data-testid="referral-expiry-run-button"
            className="mt-4 bg-white border border-slate-200 hover:border-slate-500 text-slate-700 rounded-lg px-4 py-1.5 text-xs font-medium transition-colors inline-flex items-center gap-1.5"
          >
            {running.expiry ? <Loader2 className="w-3 h-3 animate-spin"/> : <Calendar className="w-3 h-3"/>}
            Esegui ora
          </button>
        </div>
      </div>
    </div>
  );
}


function MaintenancePanel({ onCleanupDone }) {
  const [cleaning, setCleaning] = useState(false);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const cleanupDemo = async () => {
    if (!confirm(
      "Eliminare DEFINITIVAMENTE i dati demo?\n\n" +
      "Verrà rimosso l'utente di test mario.rossi@example.com, tutti i suoi referrals e i crediti associati.\n\n" +
      "Questa operazione NON è reversibile."
    )) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/maintenance/cleanup-demo");
      const d = data.deleted || {};
      toast.success(
        `Pulizia completata · utente: ${d.user} · referrals: ${(d.referrals_as_referrer||0) + (d.extra_demo_referrals||0)} · crediti: ${d.credits}`,
        { duration: 8000 }
      );
      if (onCleanupDone) await onCleanupDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante la pulizia");
    } finally {
      setCleaning(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdNew !== pwdConfirm) {
      toast.error("Le due nuove password non coincidono");
      return;
    }
    if (pwdNew.length < 12) {
      toast.error("La nuova password deve avere almeno 12 caratteri");
      return;
    }
    setPwdSubmitting(true);
    try {
      await api.post("/admin/change-password", {
        current_password: pwdCurrent,
        new_password: pwdNew,
      });
      toast.success("Password aggiornata. Effettua di nuovo il login con la nuova password.", { duration: 10000 });
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      setShowPwdForm(false);
      // Force logout after 2s so user must re-login with new password
      setTimeout(() => { window.location.href = "/login"; }, 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore cambio password");
    } finally {
      setPwdSubmitting(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="maintenance-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Manutenzione & sicurezza</div>
        <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">Solo admin</span>
      </div>
      <p className="text-sm text-slate-500 mb-5">Azioni sensibili sul database e sul tuo account.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cambio password */}
        <div className="border border-slate-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <KeyRound className="w-5 h-5 text-[#0F7B85] mt-0.5 flex-shrink-0"/>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">Cambia password admin</div>
              <p className="text-xs text-slate-500 mt-1">
                Minimo 12 caratteri. Dopo il cambio dovrai effettuare nuovamente il login.
              </p>
            </div>
          </div>
          {!showPwdForm ? (
            <button
              onClick={() => setShowPwdForm(true)}
              data-testid="admin-change-pwd-btn"
              className="mt-4 bg-[#0F7B85] hover:bg-[#0c6973] text-white rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"
            >
              <KeyRound className="w-4 h-4"/>
              Cambia password
            </button>
          ) : (
            <form onSubmit={changePassword} className="mt-4 space-y-3" data-testid="admin-change-pwd-form">
              <input
                type="password"
                placeholder="Password attuale"
                value={pwdCurrent}
                onChange={(e) => setPwdCurrent(e.target.value)}
                required
                data-testid="admin-pwd-current"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0F7B85]"
                autoComplete="current-password"
              />
              <input
                type="password"
                placeholder="Nuova password (min. 12 caratteri)"
                value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                required
                minLength={12}
                data-testid="admin-pwd-new"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0F7B85]"
                autoComplete="new-password"
              />
              <input
                type="password"
                placeholder="Conferma nuova password"
                value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                required
                minLength={12}
                data-testid="admin-pwd-confirm"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0F7B85]"
                autoComplete="new-password"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={pwdSubmitting}
                  data-testid="admin-pwd-submit"
                  className="bg-[#0F7B85] hover:bg-[#0c6973] disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"
                >
                  {pwdSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <KeyRound className="w-4 h-4"/>}
                  Salva nuova password
                </button>
                <button
                  type="button"
                  onClick={() => { setShowPwdForm(false); setPwdCurrent(""); setPwdNew(""); setPwdConfirm(""); }}
                  className="text-slate-600 hover:text-slate-900 px-3 py-2 text-sm"
                >
                  Annulla
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Cleanup dati demo */}
        <div className="border border-rose-200 bg-rose-50/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0"/>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">Pulisci dati demo</div>
              <p className="text-xs text-slate-500 mt-1">
                Elimina l&apos;utente di test <code className="bg-white px-1 py-0.5 rounded text-rose-700 text-[11px]">mario.rossi@example.com</code> e
                tutte le segnalazioni con email <code className="bg-white px-1 py-0.5 rounded text-rose-700 text-[11px]">@example.com</code>.
                Da usare una sola volta, dopo il go-live.
              </p>
            </div>
          </div>
          <button
            onClick={cleanupDemo}
            disabled={cleaning}
            data-testid="admin-cleanup-demo-btn"
            className="mt-4 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"
          >
            {cleaning ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
            Elimina dati demo
          </button>
        </div>
      </div>
    </div>
  );
}


function ProgramEnrollPanel() {
  const [text, setText] = useState("");
  const [csvRows, setCsvRows] = useState([]);          // parsed from file
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [enrolled, setEnrolled] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showList, setShowList] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  const parseEmails = (raw) => {
    return Array.from(new Set(
      raw.split(/[\s,;\n]+/)
         .map(e => e.trim().toLowerCase())
         .filter(e => e && e.includes("@"))
    ));
  };

  const emails = parseEmails(text);
  const hasFile = csvRows.length > 0;
  const totalRows = hasFile ? csvRows.length : emails.length;

  // Simple CSV parser: handles ',' or ';' separator + quoted fields.
  // Tollerante a BOM UTF-8, header capitalizzati, accenti, e variazioni di nomi.
  const parseCsv = (content) => {
    // Strip BOM if present (Excel UTF-8 export adds it)
    if (content.charCodeAt(0) === 0xFEFF) content = content.slice(1);
    const lines = content.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return [];
    const detectSep = (line) => {
      const c = (line.match(/,/g) || []).length;
      const s = (line.match(/;/g) || []).length;
      const t = (line.match(/\t/g) || []).length;
      if (t > c && t > s) return "\t";
      return s > c ? ";" : ",";
    };
    const sep = detectSep(lines[0]);
    const splitRow = (line) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i=0;i<line.length;i++) {
        const ch = line[i];
        if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; continue; }
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === sep && !inQ) { out.push(cur.trim()); cur = ""; continue; }
        cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    // Normalize header: lowercase, strip accents, replace any non-alnum with underscore
    const normHeader = (h) => h.toLowerCase().trim()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip accents
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    const headers = splitRow(lines[0]).map(normHeader);
    const aliasMap = {
      // email
      email: "email", e_mail: "email", mail: "email", indirizzo_email: "email", indirizzo: "email",
      // name
      nome: "nome", name: "nome", nome_cliente: "nome", cliente: "nome",
      nome_e_cognome: "nome", nominativo: "nome",
      // plate
      targa: "targa", plate: "targa", targa_veicolo: "targa",
      // expiry
      scadenza_rca: "scadenza_rca", scadenza: "scadenza_rca", data_scadenza: "scadenza_rca",
      expiry: "scadenza_rca", scadenza_polizza: "scadenza_rca", scadenza_rca_polizza: "scadenza_rca",
    };
    const mapped = headers.map(h => aliasMap[h] || h);
    if (!mapped.includes("email")) {
      throw new Error(`Colonna "email" non trovata. Header letti: ${headers.join(", ")}`);
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = splitRow(lines[i]);
      if (cells.every(c => !c)) continue;
      const obj = {};
      mapped.forEach((key, idx) => { if (cells[idx] !== undefined) obj[key] = cells[idx]; });
      if (obj.email && obj.email.includes("@")) {
        obj.email = obj.email.toLowerCase();
        // normalize date if Italian dd/mm/yyyy
        if (obj.scadenza_rca && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(obj.scadenza_rca)) {
          const [d, m, y] = obj.scadenza_rca.split("/");
          obj.scadenza_rca = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        }
        rows.push(obj);
      }
    }
    return rows;
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseCsv(ev.target.result);
        setCsvRows(rows);
        setText("");
        if (rows.length === 0) {
          toast.error("Nessuna riga valida trovata. Verifica che ci sia almeno una colonna 'email' nell'intestazione.", { duration: 10000 });
        } else {
          toast.success(`${rows.length} righe valide rilevate nel CSV`);
        }
      } catch (err) {
        toast.error("CSV non valido: " + err.message, { duration: 12000 });
      }
    };
    reader.readAsText(f);
  };

  const clearFile = () => { setCsvRows([]); setFileName(""); };

  const loadEnrolled = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/referral-program/status");
      setEnrolled(data || []);
    } catch (e) {
      toast.error("Errore caricamento iscritti");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { if (showList) loadEnrolled(); /* eslint-disable-next-line */ }, [showList]);

  const submit = async (e) => {
    e.preventDefault();
    if (totalRows === 0) {
      toast.error("Inserisci almeno una email valida o carica un CSV");
      return;
    }
    const msg = hasFile
      ? `Iscrivere ${totalRows} cliente${totalRows>1?'i':''} dal CSV "${fileName}"?\n\nKickoff inviato SUBITO. Le 4 email successive partono automaticamente nei 12 mesi.`
      : `Iscrivere ${totalRows} cliente${totalRows>1?'i':''} al programma?\n\nKickoff inviato SUBITO. Le 4 email successive partono automaticamente nei 12 mesi.`;
    if (!confirm(msg)) return;
    setSubmitting(true);
    setResult(null);
    try {
      const payload = hasFile
        ? { entries: csvRows, update_existing: updateExisting }
        : { emails, update_existing: updateExisting };
      const { data } = await api.post("/admin/referral-program/bulk-enroll", payload);
      setResult(data);
      const n = (data.enrolled || []).length;
      const nf = (data.not_found || []).length;
      const sk = (data.skipped || []).length;
      const up = (data.updated || []).length;
      const cr = (data.created || []).length;
      const parts = [`${n} iscritti`];
      if (cr) parts.push(`${cr} nuovi account`);
      if (up) parts.push(`${up} aggiornati`);
      parts.push(`${sk} saltati`);
      if (nf) parts.push(`${nf} non trovati`);
      toast.success(parts.join(" · "), { duration: 8000 });
      if (n > 0) { setText(""); clearFile(); }
      if (showList) loadEnrolled();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore iscrizione massiva");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="program-enroll-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Programma &quot;Presenta un Amico&quot; · Iscrizioni</div>
        <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">5 email · 12 mesi</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Carica un CSV/Excel con i clienti a cui hai rinnovato la RCA oggi, oppure incolla solo le email.
        Parte automaticamente la sequenza di 5 email su 12 mesi.
      </p>

      {/* CSV instructions */}
      <details className="mb-4 group" data-testid="csv-format-help">
        <summary className="cursor-pointer text-xs text-[#0F7B85] hover:underline font-semibold inline-flex items-center gap-1.5">
          <Inbox className="w-3.5 h-3.5"/> Formato CSV richiesto (click per vedere)
        </summary>
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono overflow-x-auto">
          <div className="text-slate-500 mb-1">Header riga 1 (separatore: virgola o punto e virgola):</div>
          <div className="text-slate-800">email,nome,targa,scadenza_rca</div>
          <div className="text-slate-500 mt-3 mb-1">Esempio:</div>
          <div className="text-slate-800">
            mario.rossi@example.it,Mario Rossi,AB123CD,2027-08-15<br/>
            lucia.bianchi@example.it,Lucia Bianchi,EF456GH,15/09/2027<br/>
            giulio.verdi@example.it,,,2027-10-01
          </div>
          <div className="text-slate-500 mt-3">
            ✅ Solo <code className="bg-white px-1 rounded">email</code> obbligatorio · gli altri sono opzionali<br/>
            ✅ Date accettate: <code className="bg-white px-1 rounded">YYYY-MM-DD</code> o <code className="bg-white px-1 rounded">DD/MM/YYYY</code><br/>
            ✅ <strong>Account creato in automatico</strong> se il cliente non è ancora registrato:
            riceverà nell&apos;email kickoff un link per impostare la sua password<br/>
            ✅ Da Excel: <strong>Salva come → CSV (UTF-8)</strong> prima di caricare
          </div>
        </div>
      </details>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="program-enroll-form">
        <div className="md:col-span-2 space-y-3">
          {/* File upload */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Carica file CSV</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                data-testid="program-csv-file"
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0F7B85] file:text-white hover:file:bg-[#0c6973] file:cursor-pointer"
              />
              {hasFile && (
                <button type="button" onClick={clearFile} data-testid="program-csv-clear"
                        className="text-rose-600 hover:text-rose-800 p-1.5 rounded hover:bg-rose-50">
                  <X className="w-4 h-4"/>
                </button>
              )}
            </div>
            {hasFile && (
              <p className="text-xs text-emerald-700 mt-1.5 font-medium">
                ✓ {fileName} · {csvRows.length} righe pronte
              </p>
            )}
          </div>

          {!hasFile && (
            <div>
              <div className="relative my-3 flex items-center">
                <div className="flex-1 border-t border-slate-200"></div>
                <span className="px-3 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">oppure</span>
                <div className="flex-1 border-t border-slate-200"></div>
              </div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Incolla solo le email (1 per riga)</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="program-emails-textarea"
                rows={4}
                placeholder={"mario.rossi@example.com\nlucia.bianchi@example.com\n..."}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0F7B85] resize-y"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                <strong>{emails.length}</strong> email valid{emails.length===1?'a':'e'} rilevat{emails.length===1?'a':'e'} (senza targa/scadenza).
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              data-testid="program-update-existing"
              className="rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
            />
            Aggiorna dati RCA per clienti già iscritti (multi-auto)
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="submit"
            disabled={submitting || totalRows === 0}
            data-testid="program-enroll-submit"
            className="bg-[#0F7B85] hover:bg-[#0c6973] disabled:opacity-50 text-white rounded-lg px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserPlus className="w-4 h-4"/>}
            Iscrivi {totalRows > 0 ? `(${totalRows})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setShowList(v => !v)}
            data-testid="program-show-list-btn"
            className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            <Inbox className="w-4 h-4"/>
            {showList ? "Nascondi" : "Vedi"} iscritti
          </button>
        </div>
      </form>

      {/* CSV preview */}
      {hasFile && csvRows.length > 0 && (
        <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden" data-testid="program-csv-preview">
          <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 border-b border-slate-200">
            Anteprima ({Math.min(5, csvRows.length)} su {csvRows.length} righe)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2">email</th>
                  <th className="text-left px-3 py-2">nome</th>
                  <th className="text-left px-3 py-2">targa</th>
                  <th className="text-left px-3 py-2">scadenza_rca</th>
                </tr>
              </thead>
              <tbody>
                {csvRows.slice(0, 5).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-[#0F7B85]">{r.email}</td>
                    <td className="px-3 py-1.5">{r.nome || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5 font-mono uppercase">{r.targa || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5">{r.scadenza_rca || <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="program-enroll-result">
          <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-3">
            <div className="text-[10px] text-emerald-700 uppercase font-semibold tracking-wider">Iscritti</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{result.enrolled?.length || 0}</div>
            <div className="text-[10px] text-emerald-600 mt-0.5">Email kickoff inviata</div>
          </div>
          <div className="border border-violet-200 bg-violet-50/60 rounded-lg p-3">
            <div className="text-[10px] text-violet-700 uppercase font-semibold tracking-wider">Nuovi account</div>
            <div className="text-2xl font-bold text-violet-700 mt-1">{result.created?.length || 0}</div>
            <div className="text-[10px] text-violet-600 mt-0.5">Magic link nell&apos;email</div>
          </div>
          <div className="border border-sky-200 bg-sky-50/60 rounded-lg p-3">
            <div className="text-[10px] text-sky-700 uppercase font-semibold tracking-wider">Aggiornati</div>
            <div className="text-2xl font-bold text-sky-700 mt-1">{result.updated?.length || 0}</div>
            <div className="text-[10px] text-sky-600 mt-0.5">RCA aggiornata (multi-auto)</div>
          </div>
          <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3">
            <div className="text-[10px] text-amber-700 uppercase font-semibold tracking-wider">Saltati</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{result.skipped?.length || 0}</div>
            <div className="text-[10px] text-amber-600 mt-0.5">Già iscritti / admin</div>
          </div>
          <div className="border border-rose-200 bg-rose-50/60 rounded-lg p-3">
            <div className="text-[10px] text-rose-700 uppercase font-semibold tracking-wider">Non trovati</div>
            <div className="text-2xl font-bold text-rose-700 mt-1">{result.not_found?.length || 0}</div>
            <div className="text-[10px] text-rose-600 mt-0.5 truncate" title={result.not_found?.join(", ")}>
              {result.not_found?.[0] || "—"}
            </div>
          </div>
        </div>
      )}

      {showList && (
        <div className="mt-6 border-t border-slate-200 pt-5" data-testid="program-enrolled-list">
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium text-slate-700">Clienti attualmente nel programma · {enrolled.length}</div>
            {loadingList && <Loader2 className="w-4 h-4 animate-spin text-slate-400"/>}
          </div>
          {enrolled.length === 0 && !loadingList && (
            <p className="text-sm text-slate-400 py-4">Nessun cliente iscritto al programma al momento.</p>
          )}
          {enrolled.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-3">Cliente</th>
                    <th className="text-left py-2 pr-3">Iscritto il</th>
                    <th className="text-left py-2 pr-3">Targa · scadenza</th>
                    <th className="text-left py-2 pr-3">Email inviate</th>
                    <th className="text-right py-2 pl-3">Crediti</th>
                  </tr>
                </thead>
                <tbody>
                  {enrolled.map(u => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-slate-900">{u.name || "—"}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600 text-xs">
                        {u.program_start_date?.slice(0,10)} <span className="text-slate-400">({u.elapsed_days}gg fa)</span>
                      </td>
                      <td className="py-2.5 pr-3 text-xs">
                        {u.rca_plate ? (
                          <>
                            <span className="font-mono text-[#0F7B85] uppercase">{u.rca_plate}</span>
                            {u.rca_expiry && <div className="text-slate-500">scade {u.rca_expiry}</div>}
                          </>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {(u.program_emails_sent || []).map(s => (
                            <span key={s} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full font-mono">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 pl-3 text-right">
                        <span className="font-semibold text-[#0F7B85]">{u.stats?.earned_eur || 0}€</span>
                        <div className="text-[10px] text-slate-400">{u.stats?.n_subscribed || 0}/{(u.stats?.n_subscribed || 0) + (u.stats?.remaining_slots || 0)} amici</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
